import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react"

/**
 * Anchoring model for DTA comments, encoded in each comment's `section_reference`:
 *
 * - "General" -> a general comment (not tied to a section or field)
 * - "<Section>" -> a section-level comment (e.g. "Metadata")
 * - "<Section> / <Field>" -> a field-level comment (e.g. "Metadata / Trial ID")
 *
 * The editor renders field/section markers that open a side rail focused on the matching anchor.
 * This context lets any field marker deep in the editor tree open the rail without prop-drilling.
 */

export const GENERAL_ANCHOR = "General"

/**
 * Build the `section_reference` anchor string for a field within a section.
 */
export function fieldAnchor(section: string, field: string): string {
  return `${section} / ${field}`
}

/**
 * The section portion of an anchor ("Metadata / Trial ID" -> "Metadata").
 */
function anchorSection(anchor: string): string {
  return anchor.split(" / ")[0]
}

/**
 * Maps an anchor's section name to the editor tab id that hosts it. Used to switch tabs when
 * navigating to a comment's location. Sections not listed are assumed to live on the collaboration
 * discussion list (no editor tab).
 */
const SECTION_TO_EDITOR_TAB: Record<string, string> = {
  "User Guide": "user-guide",
  "Data Ingestion Parameters": "data-ingestion",
  Metadata: "transfer-metadata",
  "Test Concepts": "test-concepts",
  "Data Quality Rules": "data-quality-rules",
  Codelists: "codelists",
}

function editorTabForAnchor(anchor: string): string | undefined {
  return SECTION_TO_EDITOR_TAB[anchorSection(anchor)]
}

interface DtaCommentsContextValue {
  /**
   * The DTA whose comments are being managed.
   */
  dtaId: string
  /**
   * DTA version for scoping comments.
   */
  version: string
  /**
   * Company recorded on comments authored here: the DTA's vendor for an external caller, "DAE" for
   * an internal one.
   */
  vendorCompany: string
  /**
   * Whether the comments rail is open.
   */
  railOpen: boolean
  /**
   * The anchor (section_reference) the rail is currently focused on, if any.
   */
  focusedAnchor: string | null
  /**
   * When set, the rail isolates to this single comment thread (with a "back to all" affordance).
   */
  isolatedCommentId: string | null
  /**
   * Open the rail, optionally focused on a specific anchor (field or section).
   */
  openRail: (anchor?: string) => void
  /**
   * Open the rail isolated to a single comment thread (e.g. from a Discussions "View comment" link
   * or clicking a card). The rail shows only that comment with a "← All comments" back link.
   */
  openRailForComment: (commentId: string) => void
  /**
   * Clear the isolated comment, returning the rail to the full grouped list.
   */
  clearIsolation: () => void
  /**
   * Close the rail.
   */
  closeRail: () => void
  /**
   * Toggle the rail open/closed.
   */
  toggleRail: () => void
  /**
   * Navigate to where an anchor lives and briefly highlight it. Switches the top-level page tab to
   * the editor and the editor sub-tab (for field/section anchors), then scrolls the element marked
   * with `data-comment-anchor="<anchor>"` into view. Used by the rail when a comment is clicked.
   */
  navigateToAnchor: (anchor: string) => void
  /**
   * Set by the editor: switches the active editor sub-tab by id (used during navigateToAnchor).
   */
  setEditorTabSwitcher: (fn: (tabId: string) => void) => void
  /**
   * Set by the page: switches the top-level page tab by id (e.g. to "editor" before an editor
   * anchor, used during navigateToAnchor so cross-tab navigation works).
   */
  setPageTabSwitcher: (fn: (tabId: string) => void) => void
}

const DtaCommentsContext = createContext<DtaCommentsContextValue | undefined>(undefined)

/**
 * Provides comments-rail open/focus state to the DTA editor subtree. Wrap the editor + rail so
 * field markers can call `openRail(anchor)` to reveal and focus a thread.
 */
export function DtaCommentsProvider({
  dtaId,
  vendorCompany,
  version,
  children,
}: {
  dtaId: string
  vendorCompany: string
  version: string
  children: ReactNode
}) {
  const [railOpen, setRailOpen] = useState(false)
  const [focusedAnchor, setFocusedAnchor] = useState<string | null>(null)
  const [isolatedCommentId, setIsolatedCommentId] = useState<string | null>(null)
  const editorTabSwitcher = useRef<((tabId: string) => void) | null>(null)
  const pageTabSwitcher = useRef<((tabId: string) => void) | null>(null)

  const openRail = useCallback((anchor?: string) => {
    setFocusedAnchor(anchor ?? null)
    setIsolatedCommentId(null)
    setRailOpen(true)
  }, [])

  const openRailForComment = useCallback((commentId: string) => {
    setIsolatedCommentId(commentId)
    setFocusedAnchor(null)
    setRailOpen(true)
  }, [])

  const clearIsolation = useCallback(() => setIsolatedCommentId(null), [])

  const closeRail = useCallback(() => setRailOpen(false), [])

  const toggleRail = useCallback(() => setRailOpen((prev) => !prev), [])

  const setEditorTabSwitcher = useCallback((fn: (tabId: string) => void) => {
    editorTabSwitcher.current = fn
  }, [])

  const setPageTabSwitcher = useCallback((fn: (tabId: string) => void) => {
    pageTabSwitcher.current = fn
  }, [])

  const navigateToAnchor = useCallback((anchor: string) => {
    const editorTab = editorTabForAnchor(anchor)

    // Locate the anchor's element (marked with data-comment-anchor) and scroll+flash it.
    const reveal = () => {
      const el = document.querySelector<HTMLElement>(
        `[data-comment-anchor="${CSS.escape(anchor)}"]`,
      )
      if (!el) return
      el.scrollIntoView({ behavior: "smooth", block: "center" })
      el.classList.add("comment-anchor-flash")
      setTimeout(() => el.classList.remove("comment-anchor-flash"), 1600)
    }

    if (editorTab) {
      // Editor-hosted anchor: switch the page tab to the editor, then the editor sub-tab, then reveal.
      pageTabSwitcher.current?.("editor")
      // Allow the editor tab to mount before switching its sub-tab and revealing.
      requestAnimationFrame(() => {
        editorTabSwitcher.current?.(editorTab)
        requestAnimationFrame(() => requestAnimationFrame(reveal))
      })
    } else {
      // Non-editor anchor (e.g. discussion list): reveal in place.
      requestAnimationFrame(reveal)
    }
  }, [])

  const value = useMemo(
    () => ({
      dtaId,
      version,
      vendorCompany,
      railOpen,
      focusedAnchor,
      isolatedCommentId,
      openRail,
      openRailForComment,
      clearIsolation,
      closeRail,
      toggleRail,
      navigateToAnchor,
      setEditorTabSwitcher,
      setPageTabSwitcher,
    }),
    [
      dtaId,
      version,
      vendorCompany,
      railOpen,
      focusedAnchor,
      isolatedCommentId,
      openRail,
      openRailForComment,
      clearIsolation,
      closeRail,
      toggleRail,
      navigateToAnchor,
      setEditorTabSwitcher,
      setPageTabSwitcher,
    ],
  )

  return <DtaCommentsContext.Provider value={value}>{children}</DtaCommentsContext.Provider>
}

/**
 * Access the comments-rail state. Returns null when used outside a DtaCommentsProvider, so field
 * markers can render harmlessly (no-op) in contexts where commenting isn't wired.
 */
export function useDtaComments(): DtaCommentsContextValue | null {
  return useContext(DtaCommentsContext) ?? null
}
